import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { STRINGS } from '../constants/strings'
import ConfirmModal from './confirmModal'
import { shareOrigin } from '../utils/shareLink'
import {
  DatePickerHeader, DEFAULT_Q_SETTINGS, NAME_MAX, QuestionSettingsPanel, Q_TYPES,
  BloomDistributionPanel, LockedFeatureDialog, autoBloomSplit, totalQFor, saCountFor,
  BLOOM_LEVELS, DEFAULT_BLOOM_DISTRIBUTION, DEFAULT_BLOOM_MARKS,
  PracticeMultiplierPanel, DEFAULT_PRACTICE_MULTIPLIER, TrialsSettingsPanel, QuestionTypeIndicatorToggle, cleanPageRanges,
  PageRangeBadge, resolveExamAccess,
} from './createExamForm'
import PageRangePicker from './pageRangePicker'
import { useTheme, useAuth, usePlatformPublicSettings } from '../hooks'
import { api } from '../data/api'
import BuyCreditsModal from './buyCreditsModal'

const S = STRINGS.admin

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function fmtSize(bytes) {
  return bytes > 1_048_576
    ? `${(bytes / 1_048_576).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`
}

function UploadZone({ pendingFiles, setPendingFiles, existingSources, maxTotalMb, showToast, pageRanges, onSetPageRanges }) {
  const [dragging, setDragging] = useState(false)
  const fileRef = useRef(null)
  const MAX_TOTAL = maxTotalMb != null ? maxTotalMb * 1024 * 1024 : null

  const existingBytes = (existingSources ?? []).reduce((s, src) => s + (src.size ?? 0), 0)
  const pendingBytes  = pendingFiles.reduce((s, f) => s + f.size, 0)
  const totalBytes    = existingBytes + pendingBytes
  const limitReached  = MAX_TOTAL != null && existingBytes >= MAX_TOTAL

  function addFiles(fileList) {
    if (limitReached) { showToast(`Size limit of ${maxTotalMb} MB reached — remove existing files to add more.`, 'error'); return }
    const pdfs = Array.from(fileList).filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'))
    if (!pdfs.length) { showToast(S.onlyPdf, 'error'); return }

    if (MAX_TOTAL != null) {
      const existing = new Set(pendingFiles.map(f => f.name))
      const newFiles = pdfs.filter(f => !existing.has(f.name))
      const toAdd = []
      let running = existingBytes + pendingBytes
      let hitLimit = false
      for (const f of newFiles) {
        if (running + f.size <= MAX_TOTAL) { toAdd.push(f); running += f.size }
        else hitLimit = true
      }
      if (hitLimit) showToast(`Size limit of ${maxTotalMb} MB reached — some files were not added.`, 'error')
      if (!toAdd.length) return
      setPendingFiles(prev => [...prev, ...toAdd])
    } else {
      setPendingFiles(prev => {
        const existing = new Set(prev.map(f => f.name))
        return [...prev, ...pdfs.filter(f => !existing.has(f.name))]
      })
    }
  }

  return (
    <div>
      {MAX_TOTAL != null && (
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
            <div className="h-full rounded-full transition-all duration-300"
                 style={{ width: `${Math.min(100, (totalBytes / MAX_TOTAL) * 100)}%`,
                          background: limitReached ? '#f87171' : totalBytes / MAX_TOTAL > 0.8 ? '#fbbf24' : 'var(--brand)' }} />
          </div>
          <span className="text-xs flex-shrink-0" style={{ color: limitReached ? '#f87171' : 'var(--text-3)' }}>
            {fmtSize(totalBytes)} / {maxTotalMb} MB
          </span>
        </div>
      )}
      {limitReached ? (
        <div className="flex flex-col items-center justify-center h-20 rounded-xl border-2 border-dashed"
             style={{ borderColor: 'rgba(248,113,113,0.4)', background: 'rgba(248,113,113,0.05)' }}>
          <span className="text-sm font-medium" style={{ color: '#f87171' }}>Size limit reached ({maxTotalMb} MB)</span>
          <span className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>Remove existing files to add more</span>
        </div>
      ) : (
        <label
          className={`flex flex-col items-center justify-center h-28 rounded-xl border-2 border-dashed transition-all cursor-pointer ${
            dragging
              ? 'border-[var(--brand)] bg-[var(--brand)]/10'
              : 'border-[var(--border-2)] bg-[var(--surface)] hover:border-[var(--brand)]/40 hover:bg-[var(--brand)]/5'
          }`}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files) }}
        >
          <span className="text-xl mb-1.5">{dragging ? '📂' : '📎'}</span>
          <span className="text-sm font-medium text-[var(--text-2)]">{dragging ? S.dropActive : S.dropIdle}</span>
          <span className="text-xs text-[var(--text-3)] mt-0.5">{S.dropSub}</span>
          <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden"
            onChange={e => { addFiles(e.target.files); e.target.value = '' }} />
        </label>
      )}

      {pendingFiles.length > 0 && (
        <div className="mt-2 space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-xs text-[var(--text-3)]">{pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''} selected · {fmtSize(pendingBytes)}</p>
            <button onClick={() => setPendingFiles([])} className="text-xs text-[var(--text-3)] hover:text-red-400 transition">{S.clearAll}</button>
          </div>
          {pendingFiles.map(file => (
            <div key={file.name} className="card px-4 py-2.5 flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-[var(--surface-2)] border border-[var(--border)] flex items-center justify-center text-sm flex-shrink-0">📄</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-1)] truncate">{file.name}</p>
                <p className="text-xs text-[var(--text-3)]">{fmtSize(file.size)}</p>
              </div>
              {onSetPageRanges && (
                <PageRangeBadge name={file.name} pageRanges={pageRanges} onClick={() => onSetPageRanges(file)} />
              )}
              <button onClick={() => setPendingFiles(prev => prev.filter(f => f.name !== file.name))}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-all text-xs flex-shrink-0">✗</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function PdfList({ sources, onDelete, confirmDel, setConfirmDel, sourceKey, pageRanges, onSetPageRanges }) {
  if (sources.length === 0) return null
  const pending = confirmDel ? sources.find(s => confirmDel === `${sourceKey}:${s.name}`) : null
  return (
    <>
      {pending && (
        <ConfirmModal
          title="Delete PDF"
          message={S.deletePdfConfirm(pending.name)}
          confirmLabel={S.deleteBtn2}
          onConfirm={() => { onDelete(pending.name, sourceKey); setConfirmDel(null) }}
          onCancel={() => setConfirmDel(null)}
          danger
        />
      )}
      <div className="space-y-2">
        {sources.map(src => (
          <div key={src.name} className="card p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center text-sm flex-shrink-0">📄</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--text-1)] truncate">{src.name}</p>
              <p className="text-xs text-[var(--text-3)]">{src.size > 0 ? fmtSize(src.size) : ''}</p>
            </div>
            {onSetPageRanges && (
              <PageRangeBadge name={src.name} pageRanges={pageRanges} onClick={() => onSetPageRanges(src)} />
            )}
            <button onClick={() => setConfirmDel(`${sourceKey}:${src.name}`)}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-all text-sm">✗</button>
          </div>
        ))}
      </div>
    </>
  )
}

function ExamCodeChip({ exam, orgName }) {
  const [copied, setCopied] = useState(false)
  const code = exam?.examCode
  if (!code) return null

  function handleCopy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // Wording lives in strings.js (S.examCodeMessage) — single source of truth
  // shared with adminPage.jsx's own exam-row share button, instead of each
  // keeping its own hand-copied template. shareOrigin() still picks the
  // right domain per environment (see utils/shareLink.js). No emoji —
  // WhatsApp Desktop on Windows mangles multi-byte characters.
  const shareText = S.examCodeMessage(exam.name, shareOrigin(), code, orgName)
  const waUrl     = `https://wa.me/?text=${encodeURIComponent(shareText)}`
  const mailUrl   = `mailto:?subject=${encodeURIComponent(S.examCodeSubject(exam.name))}&body=${encodeURIComponent(shareText)}`

  return (
    <div className="mb-6 px-4 py-3 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
      <p className="text-xs mb-2" style={{ color: 'var(--text-3)' }}>Share this code with examinees — they enter it on the home page to access this exam.</p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs" style={{ color: 'var(--text-3)' }}>Exam code</span>
        <span className="font-mono text-sm font-bold tracking-widest px-2.5 py-0.5 rounded-lg"
              style={{ background: 'var(--surface)', color: 'var(--brand)', border: '1px solid var(--border)' }}>
          {code}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg transition-all"
          style={{ background: copied ? 'rgba(52,211,153,0.1)' : 'var(--surface)', border: `1px solid ${copied ? 'rgba(52,211,153,0.3)' : 'var(--border)'}`, color: copied ? '#34d399' : 'var(--text-3)' }}
        >
          {copied ? '✓ Copied' : '⎘ Copy'}
        </button>
        <a href={waUrl} target="_blank" rel="noreferrer"
          className="w-6 h-6 rounded-md flex items-center justify-center transition-all"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: '#25d366', textDecoration: 'none' }}
          title="Share via WhatsApp">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.828L.057 23.405a.75.75 0 00.918.918l5.578-1.466A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.932 0-3.733-.505-5.288-1.388l-.38-.219-3.932 1.034 1.034-3.932-.219-.38A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
        </a>
        <a href={mailUrl}
          className="w-6 h-6 rounded-md flex items-center justify-center transition-all"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: '#f87171', textDecoration: 'none' }}
          title="Share via Email">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
        </a>
      </div>
    </div>
  )
}

export default function ExamDetail({ exam, onBack, onExamUpdate, showToast }) {
  const navigate = useNavigate()
  const [textbookSources, setTextbook]  = useState(exam.textbookSources ?? [])
  const [patternSources,  setPattern]   = useState(exam.patternSources  ?? [])
  const [confirmDel,      setConfirmDel] = useState(null)
  const [creditsBlockedMsg, setCreditsBlockedMsg] = useState(null)
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false)
  const { theme: dpTheme } = useTheme()
  const { user, updateUser } = useAuth()
  const { featureUnlockPrices } = usePlatformPublicSettings()
  const { lockedQTypeKeys, bloomsAllowed } = resolveExamAccess(user, featureUnlockPrices)
  const [lockedFeatureClicked, setLockedFeatureClicked] = useState(null)

  const [titleEditing, setTitleEditing] = useState(false)
  const [titleDraft,   setTitleDraft]   = useState(exam.name)
  const titleRef = useRef(null)

  const [examName,       setExamName]      = useState(exam.name)
  const [examEnabled,    setExamEnabled]   = useState(exam.enabled !== false)
  const [questionStatus, setQuestionStatus] = useState(exam.questionStatus)
  useEffect(() => { setQuestionStatus(exam.questionStatus) }, [exam.questionStatus])
  const [confirmToggle,  setConfirmToggle]  = useState(false)
  const [confirmDelExam, setConfirmDelExam] = useState(false)

  const [editValidFrom, setEditValidFrom] = useState(exam.validFrom ? new Date(exam.validFrom) : null)
  const [editValidTill, setEditValidTill] = useState(exam.validTill ? new Date(exam.validTill) : null)
  const [qSettings,     setQSettings]    = useState(exam.questionSettings ?? DEFAULT_Q_SETTINGS)
  const [difficulty,    setDifficulty]   = useState(exam.difficulty ?? 'Intermediate')
  const [bloomEnabled,      setBloomEnabled]      = useState(exam.bloomEnabled ?? false)
  const [bloomDistribution, setBloomDistribution] = useState(exam.bloomDistribution ?? DEFAULT_BLOOM_DISTRIBUTION)
  // Treat an exam that already has real distribution values as "touched" so editing
  // question counts later never silently overwrites what was saved; an exam that never
  // had Bloom's set up yet still gets the auto 50/25/25 fill when first enabled.
  const [bloomTouched, setBloomTouched] = useState(() => Object.values(exam.bloomDistribution ?? {}).some(v => v > 0))
  const [bloomMarks,        setBloomMarks]        = useState(exam.bloomMarks ?? DEFAULT_BLOOM_MARKS)
  const [practiceMultiplier, setPracticeMultiplier] = useState(exam.practiceMultiplier ?? DEFAULT_PRACTICE_MULTIPLIER)
  const [trialsEnabled,         setTrialsEnabled]         = useState(exam.trialsEnabled ?? true)
  const [trialLevelLockEnabled, setTrialLevelLockEnabled] = useState(exam.trialLevelLockEnabled ?? false)
  const [showQuestionTypeIndicator, setShowQuestionTypeIndicator] = useState(exam.showQuestionTypeIndicator ?? false)
  const [savingSettings, setSavingSettings] = useState(false)
  const [aiSettingsChanged, setAiSettingsChanged] = useState(false)
  // Separate from aiSettingsChanged — set only by changes that affect exclusively
  // the 4 Trial pools (multiplier, or enabling Trials when pools were never
  // generated), so saving can regenerate just those instead of the exam pool too.
  const [practiceSettingsChanged, setPracticeSettingsChanged] = useState(false)
  const [textbookPending,  setTextbookPending]  = useState([])
  const [patternPending,   setPatternPending]   = useState([])
  const [textbookToDelete, setTextbookToDelete] = useState([])
  const [patternToDelete,  setPatternToDelete]  = useState([])
  const [pageRanges,     setPageRanges]     = useState(exam.pageRanges ?? {})
  const [pageRangeTarget, setPageRangeTarget] = useState(null) // { name, source: {file} | {url} } while the picker is open

  useEffect(() => {
    if (bloomEnabled && !bloomTouched) setBloomDistribution(autoBloomSplit(totalQFor(qSettings), saCountFor(qSettings)))
  }, [qSettings, bloomEnabled, bloomTouched])

  function handleBloomToggle(next) {
    setBloomEnabled(next)
    setAiSettingsChanged(true)
    if (next && !bloomTouched) setBloomDistribution(autoBloomSplit(totalQFor(qSettings), saCountFor(qSettings)))
  }
  function handleBloomChange(next) {
    setBloomTouched(true)
    setBloomDistribution(next)
    setAiSettingsChanged(true)
  }
  function handleBloomReset() {
    setBloomTouched(false)
    setBloomDistribution(autoBloomSplit(totalQFor(qSettings), saCountFor(qSettings)))
    setAiSettingsChanged(true)
  }
  const bloomSum   = BLOOM_LEVELS.reduce((s, l) => s + (bloomDistribution[l.key] ?? 0), 0)
  const bloomValid = !bloomEnabled || (bloomSum === totalQFor(qSettings) && (bloomDistribution.create ?? 0) <= saCountFor(qSettings))

  function handleDeletePdf(name, sourceKey) {
    if (sourceKey === 'textbookSources') {
      setTextbook(prev => prev.filter(s => s.name !== name))
      setTextbookToDelete(prev => [...prev, name])
    } else {
      setPattern(prev => prev.filter(s => s.name !== name))
      setPatternToDelete(prev => [...prev, name])
    }
    setConfirmDel(null)
  }

  // Already-uploaded PDFs live at a gs:// path the browser can't fetch directly —
  // get a time-limited signed read URL first, then let pdf.js load that.
  async function openPageRangeForExisting(src) {
    try {
      const { url } = await api.get(`/api/exams/${exam.id}/pdf-url?sourceKey=textbookSources&name=${encodeURIComponent(src.name)}`)
      setPageRangeTarget({ name: src.name, source: { url } })
    } catch (err) {
      showToast(err.message || 'Could not open this PDF.', 'error')
    }
  }

  function openPageRangeForPending(file) {
    setPageRangeTarget({ name: file.name, source: { file } })
  }

  function handlePageRangeDone(ranges) {
    setPageRanges(prev => ({ ...prev, [pageRangeTarget.name]: ranges }))
    setAiSettingsChanged(true) // scopes exam-pool content — needs a full regeneration
    setPageRangeTarget(null)
  }

  async function handleDeleteExam() {
    await api.delete(`/api/exams/${exam.id}`)
    onExamUpdate({ ...exam, _deleted: true })
    onBack()
  }

  async function handleTitleSave() {
    const name = titleDraft.trim()
    if (!name || name === examName) { setTitleEditing(false); setTitleDraft(examName); return }
    try {
      await api.patch(`/api/exams/${exam.id}`, { name })
      setExamName(name)
      onExamUpdate({ ...exam, name })
      showToast(S.toastExamUpdated)
    } catch (err) { showToast(err.message, 'error') }
    setTitleEditing(false)
  }

  async function handleToggleEnabled() {
    setConfirmToggle(false)
    const next = !examEnabled
    try {
      await api.patch(`/api/exams/${exam.id}`, { enabled: next })
      setExamEnabled(next)
      onExamUpdate({ ...exam, enabled: next })
      showToast(next ? 'Exam enabled.' : 'Exam disabled.')
    } catch (err) { showToast(err.message, 'error') }
  }

  async function handleRegenerate(scope = 'all') {
    try {
      await api.post(`/api/exams/${exam.id}/generate`, { scope })
      setQuestionStatus('generating')
      onExamUpdate({ ...exam, questionStatus: 'generating' })
      showToast(scope === 'practice'
        ? "Regenerating Trial pools — we'll notify you via email once ready."
        : "Regenerating questions — we'll notify you via email once ready.", 'info')
    } catch (err) {
      if (err.code === 'INSUFFICIENT_CREDITS') setCreditsBlockedMsg(err.message)
      else showToast(err.message || 'Failed to start generation.', 'error')
    }
  }

  async function handleSaveSettings() {
    setSavingSettings(true)
    try {
      // 1. Process deletions
      for (const name of textbookToDelete)
        await api.delete(`/api/exams/${exam.id}/sources`, { sourceKey: 'textbookSources', name })
      if (textbookToDelete.length) setTextbookToDelete([])

      for (const name of patternToDelete)
        await api.delete(`/api/exams/${exam.id}/sources`, { sourceKey: 'patternSources', name })
      if (patternToDelete.length) setPatternToDelete([])

      // 2. Upload new textbook PDFs
      const uploadedTextbook = []
      for (const file of textbookPending) {
        const { uploadUrl, filePath } = await api.post('/api/upload/signed-url', {
          type: 'examPdf', examId: exam.id, filename: file.name, contentType: 'application/pdf',
        })
        await api.putFile(uploadUrl, file, null, { contentType: 'application/pdf' })
        const { source } = await api.post(`/api/exams/${exam.id}/confirm-pdf`, {
          filePath, name: file.name, sourceKey: 'textbookSources', size: file.size,
        })
        uploadedTextbook.push(source)
      }
      if (uploadedTextbook.length) { setTextbook(prev => [...prev, ...uploadedTextbook]); setTextbookPending([]) }

      // 3. Upload new sample PDFs
      const uploadedPattern = []
      for (const file of patternPending) {
        const { uploadUrl, filePath } = await api.post('/api/upload/signed-url', {
          type: 'examPdf', examId: exam.id, filename: file.name, contentType: 'application/pdf',
        })
        await api.putFile(uploadUrl, file, null, { contentType: 'application/pdf' })
        const { source } = await api.post(`/api/exams/${exam.id}/confirm-pdf`, {
          filePath, name: file.name, sourceKey: 'patternSources', size: file.size,
        })
        uploadedPattern.push(source)
      }
      if (uploadedPattern.length) { setPattern(prev => [...prev, ...uploadedPattern]); setPatternPending([]) }

      // 4. Save settings
      await api.patch(`/api/exams/${exam.id}`, {
        validFrom: editValidFrom?.toISOString() ?? null,
        validTill: editValidTill ? (() => { const d = new Date(editValidTill); d.setHours(23,59,59,999); return d.toISOString() })() : null,
        questionSettings: qSettings,
        difficulty,
        bloomEnabled,
        bloomDistribution: bloomEnabled ? bloomDistribution : undefined,
        bloomMarks: bloomEnabled ? bloomMarks : undefined,
        practiceMultiplier,
        trialsEnabled, trialLevelLockEnabled: trialsEnabled && trialLevelLockEnabled,
        showQuestionTypeIndicator,
        pageRanges: cleanPageRanges(pageRanges),
      })
      onExamUpdate({
        ...exam,
        validFrom: editValidFrom?.toISOString() ?? null,
        validTill: editValidTill ? (() => { const d = new Date(editValidTill); d.setHours(23,59,59,999); return d.toISOString() })() : null,
        bloomEnabled, bloomDistribution: bloomEnabled ? bloomDistribution : undefined, bloomMarks: bloomEnabled ? bloomMarks : undefined,
        practiceMultiplier,
        trialsEnabled, trialLevelLockEnabled: trialsEnabled && trialLevelLockEnabled,
        showQuestionTypeIndicator,
        pageRanges: cleanPageRanges(pageRanges),
      })

      // 5. Regenerate if anything AI-affecting changed — full regen for anything
      // touching the exam pool's content, practice-only regen when only the
      // multiplier (or newly-enabling Trials) changed, so the exam pool's
      // already-correct content isn't wastefully regenerated too.
      const needsFullRegen = textbookToDelete.length > 0 || patternToDelete.length > 0 ||
                         uploadedTextbook.length > 0  || uploadedPattern.length > 0  ||
                         aiSettingsChanged
      if (needsFullRegen) {
        setAiSettingsChanged(false)
        setPracticeSettingsChanged(false)
        await handleRegenerate('all')
      } else if (practiceSettingsChanged) {
        setPracticeSettingsChanged(false)
        await handleRegenerate('practice')
      } else {
        showToast(S.toastSettingsSaved)
      }
    } catch (err) {
      if (err.code === 'INSUFFICIENT_CREDITS') setCreditsBlockedMsg(err.message)
      else showToast(err.message, 'error')
    }
    finally { setSavingSettings(false) }
  }

  return (
    <div className="animate-fade-up">
      {creditsBlockedMsg && (
        <ConfirmModal
          title="Insufficient Credit Balance"
          message={creditsBlockedMsg}
          confirmLabel="Buy Credits"
          cancelLabel="Close"
          danger
          confirmStyle={{ background: 'var(--brand)', color: '#fff', border: 'none' }}
          onConfirm={() => { setCreditsBlockedMsg(null); setBuyCreditsOpen(true) }}
          onCancel={() => setCreditsBlockedMsg(null)}
        />
      )}
      {buyCreditsOpen && (
        <BuyCreditsModal user={user} updateUser={updateUser} showToast={showToast} onClose={() => setBuyCreditsOpen(false)} />
      )}
      {confirmToggle && (
        <ConfirmModal
          title={examEnabled ? 'Disable Exam' : 'Enable Exam'}
          message={examEnabled
            ? `Disable "${examName}"? Users will not be able to access this exam until it is re-enabled.`
            : `Enable "${examName}"? Users will be able to access this exam again.`}
          confirmLabel={examEnabled ? 'Disable' : 'Enable'}
          cancelLabel={S.cancel}
          danger={examEnabled}
          onConfirm={handleToggleEnabled}
          onCancel={() => setConfirmToggle(false)}
        />
      )}
      {confirmDelExam && (
        <ConfirmModal
          title={S.deleteExamTitle}
          message={S.deleteExamConfirm(examName)}
          confirmLabel={S.deleteExamBtn}
          cancelLabel={S.cancel}
          danger
          onConfirm={handleDeleteExam}
          onCancel={() => setConfirmDelExam(false)}
        />
      )}

      <button onClick={onBack} className="btn-ghost mb-6 text-sm">{S.backToExams}</button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--brand)' }}>{S.examLabel}</p>
          {titleEditing ? (
            <input
              ref={titleRef}
              autoFocus
              value={titleDraft}
              maxLength={NAME_MAX}
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={e => { if (e.key === 'Enter') handleTitleSave(); if (e.key === 'Escape') { setTitleEditing(false); setTitleDraft(examName) } }}
              className="w-full text-2xl sm:text-3xl font-black tracking-tight bg-transparent border-b-2 outline-none pb-0.5 transition-colors"
              style={{ color: 'var(--text-1)', borderColor: 'var(--brand)' }}
            />
          ) : (
            <h2
              className="text-2xl sm:text-3xl font-black tracking-tight cursor-text group flex items-center gap-2"
              style={{ color: 'var(--text-1)' }}
              onClick={() => { setTitleDraft(examName); setTitleEditing(true) }}
            >
              {examName}
              <span className="text-sm opacity-0 group-hover:opacity-40 transition-opacity" style={{ color: 'var(--text-3)' }}>✎</span>
            </h2>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 mt-1">
          {questionStatus && questionStatus !== 'ready' ? (
            <div className="flex items-center gap-1.5">
              {questionStatus === 'generating' && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border"
                  style={{ background: 'rgba(251,191,36,0.08)', borderColor: 'rgba(251,191,36,0.3)', color: '#fbbf24' }}>
                  <span className="w-3 h-3 rounded-full border-2 border-amber-500/30 border-t-amber-400 animate-spin flex-shrink-0" />
                  Generating…
                </span>
              )}
              {questionStatus === 'failed' && (
                <>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border"
                    title={exam.questionError || undefined}
                    style={{ background: 'rgba(248,113,113,0.08)', borderColor: 'rgba(248,113,113,0.3)', color: '#f87171' }}>
                    ✗ Gen Failed
                  </span>
                  {exam.questionError && (
                    <span className="text-xs max-w-xs truncate" style={{ color: '#f87171' }} title={exam.questionError}>
                      {exam.questionError}
                    </span>
                  )}
                  <button onClick={() => handleRegenerate('all')}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-all"
                    style={{ background: 'rgba(248,113,113,0.08)', borderColor: 'rgba(248,113,113,0.3)', color: '#f87171' }}>
                    Retry
                  </button>
                </>
              )}
              {questionStatus === 'pending' && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border"
                  style={{ background: 'rgba(148,163,184,0.08)', borderColor: 'rgba(148,163,184,0.2)', color: '#94a3b8' }}>
                  ○ No Questions
                </span>
              )}
            </div>
          ) : (
            <button
              onClick={() => setConfirmToggle(true)}
              title={examEnabled ? 'Disable exam' : 'Enable exam'}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all"
              style={examEnabled
                ? { background: 'rgba(52,211,153,0.08)', borderColor: 'rgba(52,211,153,0.3)', color: '#34d399' }
                : { background: 'rgba(248,113,113,0.08)', borderColor: 'rgba(248,113,113,0.3)', color: '#f87171' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: examEnabled ? '#34d399' : '#f87171' }} />
              {examEnabled ? 'Active' : 'Inactive'}
            </button>
          )}
          <button title={S.deleteTitle} onClick={() => setConfirmDelExam(true)}
            className="w-8 h-8 rounded-lg flex items-center justify-center border transition-all text-sm"
            style={{ background: 'var(--surface-2)', borderColor: 'var(--border)', color: '#f87171' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#f87171'; e.currentTarget.style.background = 'rgba(248,113,113,0.08)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface-2)' }}
          >✗</button>
        </div>
      </div>

      {/* Exam code */}
      <ExamCodeChip exam={exam} orgName={user?.name} />


      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        {[
          { v: textbookSources.length, l: S.statTextbookPdfs, c: '#fbbf24' },
          { v: patternSources.length,  l: S.statPatternPdfs,  c: 'var(--brand)' },
          { v: fmtDate(exam.createdAt),l: S.statCreatedLabel, c: 'var(--text-1)' },
        ].map(({ v, l, c }) => (
          <div key={l} className="card p-4 text-center">
            <div className="font-bold text-lg leading-tight" style={{ color: c }}>{v}</div>
            <div className="text-[0.7rem] text-[var(--text-3)] mt-0.5">{l}</div>
          </div>
        ))}
      </div>

      {/* Textbook PDFs */}
      <section className="mb-8">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-[var(--text-1)]">{S.textbookPdfsLabel}</h3>
          <p className="text-xs text-[var(--text-3)] mt-0.5">{S.textbookPdfsDesc}</p>
        </div>
        {textbookSources.length > 0 && (
          <p className="text-[0.65rem] font-semibold uppercase tracking-widest text-[var(--text-3)] mb-2">Uploaded</p>
        )}
        <PdfList sources={textbookSources} onDelete={handleDeletePdf} confirmDel={confirmDel} setConfirmDel={setConfirmDel} sourceKey="textbookSources"
          pageRanges={pageRanges} onSetPageRanges={openPageRangeForExisting} />
        <div className="mt-3">
          <p className="text-[0.65rem] font-semibold uppercase tracking-widest text-[var(--text-3)] mb-2">Add new</p>
          <UploadZone pendingFiles={textbookPending} setPendingFiles={setTextbookPending} existingSources={textbookSources} maxTotalMb={15} showToast={showToast}
            pageRanges={pageRanges} onSetPageRanges={openPageRangeForPending} />
        </div>
      </section>

      {/* Sample Question PDFs */}
      <section className="mb-8">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-[var(--text-1)]">{S.patternPdfsLabel}</h3>
          <p className="text-xs text-[var(--text-3)] mt-0.5">{S.patternPdfsDesc}</p>
        </div>
        {patternSources.length > 0 && (
          <p className="text-[0.65rem] font-semibold uppercase tracking-widest text-[var(--text-3)] mb-2">Uploaded</p>
        )}
        <PdfList sources={patternSources} onDelete={handleDeletePdf} confirmDel={confirmDel} setConfirmDel={setConfirmDel} sourceKey="patternSources" />
        <div className="mt-3">
          <p className="text-[0.65rem] font-semibold uppercase tracking-widest text-[var(--text-3)] mb-2">Add new</p>
          <UploadZone pendingFiles={patternPending} setPendingFiles={setPatternPending} existingSources={patternSources} maxTotalMb={5} showToast={showToast} />
        </div>
      </section>

      {/* Exam Settings */}
      <section className="mb-8">
        <h3 className="text-sm font-semibold text-[var(--text-1)] mb-4">Exam Settings</h3>

        <QuestionSettingsPanel
          settings={qSettings}
          onChange={v => { setQSettings(v); setAiSettingsChanged(true) }}
          difficulty={difficulty}
          onDifficultyChange={v => { setDifficulty(v); setAiSettingsChanged(true) }}
          lockedKeys={lockedQTypeKeys}
          onDisallowedClick={key => setLockedFeatureClicked({ key, label: Q_TYPES.find(t => t.key === key)?.label ?? key })}
        />

        <p className="text-[0.68rem] font-bold uppercase tracking-widest mt-4 mb-2" style={{ color: 'var(--text-3)' }}>Configurations</p>

        <div>
          <BloomDistributionPanel
            enabled={bloomEnabled} onToggle={handleBloomToggle}
            distribution={bloomDistribution} onChange={handleBloomChange} onReset={handleBloomReset}
            marks={bloomMarks} onMarksChange={v => { setBloomMarks(v); setAiSettingsChanged(true) }}
            questionSettings={qSettings}
            allowed={bloomsAllowed} onDisallowedClick={() => setLockedFeatureClicked({ key: 'bloomsTaxonomy', label: "Bloom's Taxonomy" })}
          />
        </div>

        <div className="mt-4">
          <QuestionTypeIndicatorToggle value={showQuestionTypeIndicator} onChange={setShowQuestionTypeIndicator} />
        </div>

        <div className="mt-4">
          <TrialsSettingsPanel
            trialsEnabled={trialsEnabled}
            onTrialsEnabledChange={v => {
              setTrialsEnabled(v)
              // Disabling never needs regeneration — the existing pools just stop
              // being served. Enabling only needs it if the practice pools were
              // never actually generated (created with Trials off, so they're
              // still 'skipped'/'pending' rather than 'ready').
              if (v) {
                const alreadyGenerated = ['beginner', 'intermediate', 'advanced', 'expert']
                  .every(level => exam.poolStatus?.[level] === 'ready')
                // Only the 4 practice pools need generating here — the exam pool
                // already exists, so this doesn't need a full regeneration.
                if (!alreadyGenerated) setPracticeSettingsChanged(true)
              }
            }}
            trialLevelLockEnabled={trialLevelLockEnabled}
            onTrialLevelLockChange={v => setTrialLevelLockEnabled(v)}
          />
        </div>

        {trialsEnabled && (
          <div className="mt-4">
            <PracticeMultiplierPanel value={practiceMultiplier} onChange={v => { setPracticeMultiplier(v); setPracticeSettingsChanged(true) }} totalQ={totalQFor(qSettings)} />
          </div>
        )}

        {lockedFeatureClicked && (
          <LockedFeatureDialog feature={lockedFeatureClicked} orgName={user?.name} price={featureUnlockPrices?.[lockedFeatureClicked.key] ?? 0}
            onBuyCredits={() => setBuyCreditsOpen(true)} onClose={() => setLockedFeatureClicked(null)} />
        )}

        {pageRangeTarget && (
          <PageRangePicker
            fileName={pageRangeTarget.name}
            source={pageRangeTarget.source}
            initialRanges={pageRanges[pageRangeTarget.name]}
            onDone={handlePageRangeDone}
            onClose={() => setPageRangeTarget(null)}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="text-xs font-medium text-[var(--text-3)] mb-1.5 block">Valid From</label>
            <DatePicker selected={editValidFrom}
              onChange={d => { setEditValidFrom(d); if (editValidTill && d > editValidTill) setEditValidTill(null) }}
              minDate={exam.validFrom ? new Date(exam.validFrom) : new Date()}
              dateFormat="dd/MM/yyyy" placeholderText="dd/mm/yyyy"
              renderCustomHeader={props => <DatePickerHeader {...props} />}
              className="w-full px-3 py-2 rounded-xl text-sm border transition focus:outline-none cursor-pointer"
              wrapperClassName="w-full" calendarClassName={dpTheme === 'dark' ? 'dp-dark' : ''} portalId="dp-portal" />
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-3)] mb-1.5 block">Valid Till</label>
            <DatePicker selected={editValidTill}
              onChange={d => setEditValidTill(d)}
              minDate={editValidFrom ?? undefined} dateFormat="dd/MM/yyyy" placeholderText="dd/mm/yyyy"
              renderCustomHeader={props => <DatePickerHeader {...props} />}
              className="w-full px-3 py-2 rounded-xl text-sm border transition focus:outline-none cursor-pointer"
              wrapperClassName="w-full" calendarClassName={dpTheme === 'dark' ? 'dp-dark' : ''} portalId="dp-portal" />
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4">
          <button onClick={handleSaveSettings} disabled={savingSettings || !bloomValid} className="btn-primary text-sm disabled:opacity-50">
            {savingSettings
              ? <><span className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Updating…</>
              : 'Update'}
          </button>
        </div>
      </section>
    </div>
  )
}
